import type { CustomerMonthlyRollup } from '@/api/types'

/** Default top-N named bars before the Others rollup (system setting override). */
export const DEFAULT_TOP_CUSTOMERS = 10
/** @deprecated Prefer DEFAULT_TOP_CUSTOMERS / resolveTopCustomers. */
export const TOP_CUSTOMERS = DEFAULT_TOP_CUSTOMERS
export const OTHERS_ID = '__others__'

/** Clamp a configured top-N (system ``customer_bar_top_n``) to a safe chart range. */
export function resolveTopCustomers(raw: unknown, fallback = DEFAULT_TOP_CUSTOMERS): number {
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(n)) return fallback
  return Math.min(50, Math.max(1, Math.floor(n)))
}

export type BarRow = {
  id: string
  name: string
  committed: number
  /** Weighted - committed (non-negative). */
  toWeighted: number
  /** Upside - weighted (non-negative). */
  toUpside: number
  weighted: number
  upside: number
  customer: CustomerMonthlyRollup
  /** When set (Others bucket), chart selection expands to these customer ids. */
  memberIds?: string[]
}

function truncateLabel(name: string, max = 22): string {
  if (name.length <= max) return name
  return `${name.slice(0, max - 1)}…`
}

function money(n: number | string | undefined): number {
  const v = typeof n === 'string' ? Number(n) : Number(n)
  return Number.isFinite(v) ? v : 0
}

function toBarRow(customer: CustomerMonthlyRollup): BarRow {
  const committed = money(customer.year_committed)
  const weighted = money(customer.year_weighted)
  const upside = money(customer.year_upside)
  return {
    id: customer.customer_id,
    name: truncateLabel(customer.customer_name),
    committed: Math.max(committed, 0),
    toWeighted: Math.max(weighted - committed, 0),
    toUpside: Math.max(upside - weighted, 0),
    weighted: Math.max(weighted, 0),
    upside: Math.max(upside, 0),
    customer,
  }
}

function sumRows(
  rows: BarRow[],
): Pick<BarRow, 'committed' | 'weighted' | 'upside' | 'toWeighted' | 'toUpside'> {
  const committed = rows.reduce((s, r) => s + r.committed, 0)
  const weighted = rows.reduce((s, r) => s + r.weighted, 0)
  const upside = rows.reduce((s, r) => s + r.upside, 0)
  return {
    committed,
    weighted,
    upside,
    toWeighted: Math.max(weighted - committed, 0),
    toUpside: Math.max(upside - weighted, 0),
  }
}

/** Top N by total upside; remaining accounts rolled into a single Others bar. */
export function buildCustomerBarRows(
  customers: CustomerMonthlyRollup[],
  topN = DEFAULT_TOP_CUSTOMERS,
): BarRow[] {
  const limit = resolveTopCustomers(topN)
  const ranked = customers
    .map(toBarRow)
    .filter((row) => row.upside > 0 || row.weighted > 0 || row.committed > 0)
    .sort((a, b) => b.upside - a.upside || b.weighted - a.weighted || a.name.localeCompare(b.name))

  if (ranked.length <= limit) return ranked

  const top = ranked.slice(0, limit)
  const rest = ranked.slice(limit)
  const totals = sumRows(rest)
  const othersLabel = `Others (${rest.length})`
  const others: BarRow = {
    id: OTHERS_ID,
    name: truncateLabel(othersLabel),
    ...totals,
    customer: {
      customer_id: OTHERS_ID,
      customer_name: othersLabel,
      months: [],
      year_committed: totals.committed,
      year_weighted: totals.weighted,
      year_upside: totals.upside,
    },
    memberIds: rest.map((r) => r.id),
  }
  return [...top, others]
}
